import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Settings,
  Building2,
  Palette,
  Globe,
  Mail,
  CreditCard,
  Brain,
  FileText,
  Users,
  Shield,
  Key,
  Webhook,
  Beaker,
  Clock,
  Bell,
  AlertTriangle,
  Lock,
  FileCheck,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Crown,
  Gauge,
  UserCog,
  MapPin,
  Zap,
  Layers,
  Database,
  Box,
  Tags,
  Activity,
  BarChart3,
  Plug,
  KeyRound,
  ShoppingCart
} from 'lucide-react';

/**
 * Platform Settings Layout
 * Dedicated workspace for configuration, integrations, and system setup
 * Separate from operational Platform Console
 */
const PlatformSettingsLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    integrations: true,
    plansEntitlements: true,
    dataSystem: true,
    users: false,
    plansConfig: false,
    communication: false,
    security: false,
    advanced: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    if (location.pathname.startsWith('/platform/settings/communication')) {
      setExpandedSections((prev) => ({ ...prev, communication: true }));
    }
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isInSection = (paths) => paths.some(p => location.pathname.startsWith(p));

  // Navigation sections for Platform Settings
  const settingsSections = [
    {
      id: 'general',
      title: 'General',
      icon: Settings,
      items: [
        { path: '/platform/settings/profile', label: 'Platform Profile', icon: Building2 },
        { path: '/platform/settings/branding', label: 'Platform Branding', icon: Palette },
        { path: '/platform/settings/regional', label: 'Regional Defaults', icon: Globe },
      ]
    },
    {
      id: 'plansEntitlements',
      title: 'Plans & Entitlements',
      icon: Crown,
      items: [
        { path: '/platform/settings/plans', label: 'All Plans', icon: Crown },
        { path: '/platform/settings/plans/entitlements', label: 'Entitlements', icon: FileCheck },
        { path: '/platform/settings/plans/role-templates', label: 'Role Templates', icon: UserCog },
        { path: '/platform/settings/plans/user-access', label: 'User Access', icon: Users },
        { path: '/platform/settings/plans/agency-access', label: 'Agency Access', icon: Building2 },
        { path: '/platform/settings/plans/audit', label: 'Overrides & Audit', icon: FileText },
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: Zap,
      items: [
        { path: '/platform/settings/integrations/google-address', label: 'Google Address Services', icon: MapPin },
        { path: '/platform/settings/integrations/mailgun', label: 'Mailgun', icon: Mail },
        { path: '/platform/settings/integrations/whop', label: 'Whop', icon: ShoppingCart },
        { path: '/platform/settings/integrations/postGrid', label: 'PostGrid', icon: FileText },
        { path: '/platform/settings/integrations/stripe', label: 'Stripe', icon: CreditCard },
        { path: '/platform/settings/integrations/openai', label: 'OpenAI', icon: Brain },
        { path: '/platform/settings/integrations/credit-providers', label: 'Credit Report Providers', icon: FileCheck },
      ]
    },
    {
      id: 'dataSystem',
      title: 'Data & System',
      icon: Database,
      items: [
        { path: '/platform/settings/data/objects', label: 'Objects', icon: Box },
        { path: '/platform/settings/data/custom-fields', label: 'Custom Fields', icon: FileText },
        { path: '/platform/settings/data/custom-values', label: 'Custom Values', icon: Key },
        { path: '/platform/settings/data/manage-scoring', label: 'Manage Scoring', icon: BarChart3 },
        { path: '/platform/settings/data/tags', label: 'Tags', icon: Tags },
        { path: '/platform/settings/data/external-tracking', label: 'External Tracking', icon: Activity },
        { path: '/platform/settings/data/integrations', label: 'Integrations Hub', icon: Plug },
        { path: '/platform/settings/data/private-integrations', label: 'Private Integrations', icon: KeyRound },
      ]
    },
    {
      id: 'users',
      title: 'Users & Access',
      icon: Users,
      items: [
        { path: '/platform/settings/roles', label: 'Roles & Permissions', icon: UserCog },
        { path: '/platform/settings/admin-rules', label: 'Admin Access Rules', icon: Shield },
        { path: '/platform/settings/security-policies', label: 'Security Policies', icon: Lock },
      ]
    },
    {
      id: 'plansConfig',
      title: 'Plans Configuration',
      icon: Layers,
      items: [
        { path: '/platform/settings/plan-defaults', label: 'Plan Defaults', icon: Layers },
        { path: '/platform/settings/entitlement-rules', label: 'Entitlement Rules', icon: FileCheck },
        { path: '/platform/settings/usage-limits', label: 'Usage Limits', icon: Gauge },
      ]
    },
    {
      id: 'communication',
      title: 'Communication',
      icon: Mail,
      items: [
        { path: '/platform/settings/communication/domains', label: 'Domains', icon: Globe },
        { path: '/platform/settings/communication/system-automations', label: 'System Automations', icon: Zap },
        { path: '/platform/settings/email-provider', label: 'Email Provider', icon: Mail },
        { path: '/platform/settings/notification-templates', label: 'Notification Templates', icon: Bell },
        { path: '/platform/settings/system-alerts', label: 'System Alerts', icon: AlertTriangle },
      ]
    },
    {
      id: 'security',
      title: 'Security',
      icon: Shield,
      items: [
        { path: '/platform/settings/authentication', label: 'Authentication', icon: Lock },
        { path: '/platform/settings/sessions', label: 'Sessions', icon: Clock },
        { path: '/platform/settings/audit-logs', label: 'Audit Logs', icon: FileText },
      ]
    },
    {
      id: 'advanced',
      title: 'Advanced',
      icon: Beaker,
      items: [
        { path: '/platform/settings/api-keys', label: 'API Keys', icon: Key },
        { path: '/platform/settings/webhooks', label: 'Webhooks', icon: Webhook },
        { path: '/platform/settings/feature-flags', label: 'Feature Flags', icon: Beaker },
        { path: '/platform/settings/labs', label: 'Labs', icon: Beaker },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B1F18] flex">
      {/* Settings Sidebar */}
      <aside className="w-72 min-h-screen bg-gradient-to-b from-[rgba(10,38,28,0.95)] to-[rgba(10,38,28,0.85)] border-r border-[rgba(142,218,182,0.12)] flex flex-col sticky top-0 h-screen overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-[rgba(142,218,182,0.12)] flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate('/platform/dashboard', { replace: true })}
              className="flex items-center gap-2 text-[#93AE9F] hover:text-white transition-colors mb-4"
              data-testid="platform-settings-back"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Back to Platform</span>
            </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Platform Settings</h1>
              <p className="text-xs text-[#93AE9F]">Configuration & Setup</p>
            </div>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 p-3 overflow-y-auto scrollbar-thin scrollbar-thumb-emerald-800 scrollbar-track-transparent">
          {settingsSections.map((section) => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections[section.id];
            const sectionPaths = section.items.map(item => item.path);
            const hasActiveChild = isInSection(sectionPaths);
            
            return (
              <div key={section.id} className="mb-2">
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                    hasActiveChild
                      ? 'bg-emerald-500/10 text-emerald-300'
                      : 'text-[#93AE9F] hover:text-white hover:bg-[rgba(142,218,182,0.08)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <SectionIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">{section.title}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="mt-1 ml-3 pl-3 border-l border-[rgba(142,218,182,0.1)] space-y-1">
                    {section.items.map((item) => {
                      const ItemIcon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                            active
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                              : 'text-[#93AE9F] hover:text-white hover:bg-[rgba(142,218,182,0.08)]'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-[rgba(142,218,182,0.12)] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white text-sm font-semibold">
              {user?.name?.charAt(0) || 'P'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'Platform Owner'}</p>
              <p className="text-xs text-[#93AE9F] truncate capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default PlatformSettingsLayout;
